// Utils
import { isTiptapContent } from '../../editorTypeUtils';
import { mapTiptapNodes } from '../mapTiptapNodes';

// Types
import { EditorContent } from '../../../../elements/elementModelTypes';
import { JSONContent } from '@tiptap/core';
import { TiptapContentNode, TiptapMarkType, TiptapNodeType } from '../../../tiptapTypes';

// Local types
type MarkType = NonNullable<JSONContent['marks']>[number];

type MarkMapFunction = (mark: MarkType) => MarkType | undefined;

const mapInlineCommentNode = (markMapFunction: MarkMapFunction) => (node?: TiptapContentNode) => {
    if (!node) return {};
    if (node.type !== TiptapNodeType.text) return node;
    if (!node.marks || node.marks.length === 0) return node;

    const updatedMarks = node.marks.map(markMapFunction).filter(Boolean) as MarkType[];

    if (updatedMarks.length === 0) return { ...node, marks: undefined };

    return {
        ...node,
        marks: updatedMarks,
    };
};

const updateEditorJsonInlineComment =
    (markMapFunction: MarkMapFunction) =>
    <T extends EditorContent>(content: T): T => {
        if (!isTiptapContent(content)) return content;

        // Map through content and update only necessary nodes
        const updated = mapTiptapNodes(content, mapInlineCommentNode(markMapFunction));

        // If nothing changed, mapTiptapNodes will still have returned the original reference hierarchy
        return updated as T;
    };

const mapInlineColorMark =
    (commentThreadId: string, color: string): MarkMapFunction =>
    (mark: MarkType) => {
        if (mark.type !== TiptapMarkType.inlineComment) return mark;

        const currentId = mark.attrs?.commentThreadId;
        if (currentId !== commentThreadId) return mark;

        // If color already matches, return original mark to preserve reference
        if (mark.attrs?.color === color) return mark;

        return {
            ...mark,
            attrs: {
                ...mark.attrs,
                color,
            },
        };
    };

/**
 * Updates (or sets) the color attribute on the inlineComment mark matching the provided commentThreadId.
 * If the content is not Tiptap content, the original content is returned unchanged.
 * If no matching inline comment mark is found, the original content is returned unchanged.
 */
export const updateEditorJsonInlineCommentColor = <T extends EditorContent>(
    content: T,
    commentThreadId: string,
    color: string,
): T => {
    const mapInlineColorMarkFn = mapInlineColorMark(commentThreadId, color);
    return updateEditorJsonInlineComment(mapInlineColorMarkFn)(content);
};

const mapRemoveInlineCommentMark =
    (commentThreadId: string): MarkMapFunction =>
    (mark: MarkType) => {
        if (mark.type !== TiptapMarkType.inlineComment) return mark;

        const currentId = mark.attrs?.commentThreadId;
        if (currentId !== commentThreadId) return mark;

        // Return undefined to remove this mark
        return undefined;
    };

/**
 * Removes the inlineComment mark matching the provided commentThreadId.
 */
export const removeEditorJsonInlineComment = <T extends EditorContent>(content: T, commentThreadId: string): T => {
    const mapRemoveInlineCommentMarkFn = mapRemoveInlineCommentMark(commentThreadId);
    return updateEditorJsonInlineComment(mapRemoveInlineCommentMarkFn)(content);
};
